//------------------------------------------------------------------------
// Expand/collapse functionality for animating en element’s height using CSS
//
// This is similar to jQuery’s slideToggle() method.
//
// For animating an element with transforms, not changing its height, see activate.js
//
// Minimum required styles:
//
//   #menu {
//     $menu-speed: 400ms;
//     max-height: 0;// Hide on load to prevent relayout.
//     overflow: hidden;
//     transition: max-height $menu-speed fs-easing('easeOutCubic'),
//                 visibility 0s linear 0s;
//     visibility: visible;
//
//     &[aria-hidden="true"] {
//       max-height: 0 !important;// !important required to override inline styles
//       transition: max-height $menu-speed fs-easing('easeOutCubic'),
//                   visibility 0s linear $menu-speed;
//       visibility: hidden;
//     }
//   }
//
// Markup example:
// <button type="button" data-expands="menu">Toggle Menu</button>
// <div id="menu">Menu content</div>
//
// After JS runs:
// <button type="button" data-expands="menu" aria-expanded="false" aria-controls="menu">Toggle Menu</button>
// <div id="menu" aria-hidden="true" style="max-height: 220px;">Menu content</div>
//
// Active state:
// <button type="button" data-expands="menu" aria-expanded="true" aria-controls="menu">Toggle Menu</button>
// <div id="menu" aria-hidden="false" style="max-height: 220px;">Menu content</div>
//
// See https://inclusive-components.design/menus-menu-buttons/
//------------------------------------------------------------------------
"use strict";

import debounce from "lodash/debounce";

class ExpandToggle {
  constructor(el) {
    var self = this;
    this.el = el;
    this.parentEl = this.el.parentNode;
    this.elTagName = this.el.tagName.toLowerCase();
    this.isActive = false;
    this.targetId = this.el.getAttribute("data-expands");
    this.targetEl = document.getElementById(this.targetId);
    this.activeClasses =
      this.el.getAttribute("data-expands-class") || "is-active";

    // Ensure target element exist before initializing
    if (!this.targetId.length || !this.targetEl) {
      // console.warn(`Could not find expandable target with id="${this.targetId}"`);
      return false;
    }

    // Accessibility setup
    this.el.setAttribute("aria-expanded", false);
    this.el.setAttribute("aria-controls", this.targetId);
    this.targetEl.setAttribute("aria-hidden", true);

    if (this.elTagName === "a") {
      this.el.setAttribute("role", "button");
    }

    // Set max-height to the expanded height so we can animate it.
    this.updateExpandedHeight();

    // Check if active class string contains multiple classes.
    if (this.activeClasses.indexOf(" ") > -1) {
      // Convert to array and remove any empty string values
      // caused by having multiple spaces in a row.
      this.activeClasses = this.activeClasses
        .split(" ")
        .filter(n => n.length);
    } else {
      // We still need to convert a single active class to an array
      // so we can use the spread syntax later in classList.add()
      this.activeClasses = [this.activeClasses];
    }

    // Toggle activation on click
    this.el.addEventListener("click", function() {
      self.toggle();
    });

    // Update target element max-height on resize
    window.addEventListener(
      "resize",
      debounce(function() {
        self.updateExpandedHeight();
      }, 150)
    );

    // Listen for text size change event (emitted by a11y-menu.js)
    document.documentElement.addEventListener("text-size", function() {
      // console.log('heard text-size event');
      self.updateExpandedHeight();
    });
  }

  // Set max-height of target element to its expanded height without triggering relayout.
  //
  // This technique works by creating an absolutely-positioned invisible clone of the target element
  // and calculating getting its height. This avoids any relayout that would otherwise occur if the
  // element was briefly expanded so we could measure it.
  //
  // Note: We’re using CSS to transition max-height instead jQuery’s slideToggle(),
  //       or another 3rd-party lib like Velocity.js, to avoid loading a large lib.
  updateExpandedHeight() {

    // Get width of original element so we can apply it to the clone
    var nodeWidth = Math.round(parseFloat(this.targetEl.offsetWidth));

    // Create clone of node
    var cloneEl = this.targetEl.cloneNode(true); // 'true' includes child nodes

    // Inline styles added to prevent reflow, ensure clone is same size as expanded element
    cloneEl.style.cssText =
      "max-height: none !important; position: absolute !important; right: 100% !important; visibility: hidden !important; width: " +
      nodeWidth +
      "px !important; -webkit-transition: none !important; -moz-transition: none !important; transition: none !important";

    // Update “aria-hidden” attribute
    cloneEl.setAttribute("aria-hidden", false);

    // Append clone to document, save as new var so we can remove it later
    var newEl = this.parentEl.insertBefore(cloneEl, this.targetEl);

    // Calculate height
    var expandedHeight = Math.round(parseFloat(newEl.offsetHeight));

    // Remove cloned node to clean up after ourselves
    this.parentEl.removeChild(newEl);

    // Apply inline max-height to collapsed element
    // Note: CSS is overriding this when aria-hidden="true"
    this.targetEl.style.maxHeight = expandedHeight + "px";
  }

  activate() {
    this.el.setAttribute("aria-expanded", true);
    this.el.classList.add(...this.activeClasses);
    this.targetEl.setAttribute("aria-hidden", false);
    this.targetEl.classList.add(...this.activeClasses);
    this.isActive = true;
  }

  deactivate() {
    this.el.setAttribute("aria-expanded", false);
    this.el.classList.remove(...this.activeClasses);
    this.targetEl.setAttribute("aria-hidden", true);
    this.targetEl.classList.remove(...this.activeClasses);
    this.isActive = false;
  }

  toggle() {
    if (this.isActive) {
      this.deactivate();
    } else {
      this.activate();
    }
  }
}

// Init
const nodes = document.querySelectorAll("[data-expands]");
for (var i = 0, len = nodes.length; i < len; i++) {
  new ExpandToggle(nodes[i]);
}

export default ExpandToggle;



// WEBPACK FOOTER //
// ./scripts/components/expand.js